import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { streamLiveWall, streamMyLiveWall } from '../api/liveWall';
import { LiveWallBuffer } from '../live/liveWallModel';

/** Overlaid metrics + their display labels. */
const METRICS: { key: string; label: string }[] = [
  { key: 'focus', label: 'Focus' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'hand_activity', label: 'Hand activity · |ω|' },
];

/** Distinct, legible participant colours (teal / amber / violet / rose …). */
const COLORS = ['#2dd4bf', '#f6b73c', '#a78bfa', '#fb7185', '#38bdf8', '#f472b6'];

type Status = 'connecting' | 'live' | 'reconnecting' | 'ended';

/**
 * Live Recording — live overlay of a case's participants (LIVE-OR-STREAMING).
 *
 * Subscribes to the case's SSE frame stream, feeds a {@link LiveWallBuffer}, and
 * paints one auto-scaled grid per metric with a trace per participant plus the
 * server's divergence markers, at animation cadence. A newest-first marker log
 * sits alongside.
 */
export function LiveSignalWall({
  tenantId,
  caseId,
  onEnded,
  participantLabel = (id) => id,
  onParticipantClick,
}: {
  /** The tenant whose wall this is, or null for a participant watching a
   *  case they are in (LIVE-WALL-PARTICIPANT) — the stream is then scoped to
   *  them server-side. */
  tenantId: string | null;
  caseId: string;
  onEnded?: () => void;
  /** Resolve a participant id to a display name for the legend. */
  participantLabel?: (id: string) => string;
  /** Click a legend participant to drill into their full live detail. */
  onParticipantClick?: (id: string) => void;
}) {
  const bufRef = useRef(new LiveWallBuffer());
  const canvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});
  const [status, setStatus] = useState<Status>('connecting');
  const [log, setLog] = useState<ReturnType<LiveWallBuffer['markerLog']>>([]);
  const [pids, setPids] = useState<string[]>([]);
  // Keep the latest onEnded without making it a subscribe dependency (an inline
  // arrow from the parent would otherwise re-subscribe every render).
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  // Subscribe (re-subscribes when the tenant/case changes).
  useEffect(() => {
    bufRef.current = new LiveWallBuffer();
    setStatus('connecting');
    setLog([]);
    setPids([]);
    const handlers = {
      onReady: () => setStatus('live'),
      onFrame: (f: Parameters<typeof bufRef.current.ingest>[0]) =>
        bufRef.current.ingest(f),
      onError: () => setStatus('reconnecting'),
      onEnded: () => {
        setStatus('ended');
        onEndedRef.current?.();
      },
    };
    const stop =
      tenantId === null
        ? streamMyLiveWall(caseId, handlers)
        : streamLiveWall(tenantId, caseId, handlers);
    return stop;
  }, [tenantId, caseId]);

  // Repaint the canvases every frame; refresh the React-rendered bits (marker
  // log, participant legend) on a gentler cadence.
  useEffect(() => {
    let raf = 0;
    const paint = () => {
      for (const { key } of METRICS) {
        const c = canvasRefs.current[key];
        if (c) drawMetric(c, bufRef.current, key);
      }
      raf = requestAnimationFrame(paint);
    };
    raf = requestAnimationFrame(paint);
    const slow = setInterval(() => {
      setLog(bufRef.current.markerLog());
      setPids(bufRef.current.participantIds());
    }, 500);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(slow);
    };
  }, []);

  const statusColor =
    status === 'live' ? 'success' : status === 'reconnecting' ? 'warning' : 'default';

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          color={statusColor}
          label={status}
          sx={{ textTransform: 'capitalize' }}
        />
        <Stack direction="row" spacing={1.5}>
          {pids.map((p, i) => (
            <Stack
              key={p}
              direction="row"
              spacing={0.6}
              alignItems="center"
              onClick={onParticipantClick ? () => onParticipantClick(p) : undefined}
              sx={{
                cursor: onParticipantClick ? 'pointer' : 'default',
                '&:hover': onParticipantClick
                  ? { '& .pname': { textDecoration: 'underline' } }
                  : undefined,
              }}
              title={onParticipantClick ? 'Open live detail' : undefined}
            >
              <Box
                sx={{
                  width: 14,
                  height: 3,
                  borderRadius: 1,
                  bgcolor: COLORS[i % COLORS.length],
                }}
              />
              <Typography
                className="pname"
                variant="caption"
                color={onParticipantClick ? 'primary' : 'text.secondary'}
              >
                {participantLabel(p)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {METRICS.map(({ key, label }) => (
        <Paper key={key} variant="outlined" sx={{ overflow: 'hidden' }}>
          <Typography
            variant="overline"
            sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}
          >
            {label}
          </Typography>
          <Box
            component="canvas"
            ref={(el: HTMLCanvasElement | null) => {
              canvasRefs.current[key] = el;
            }}
            sx={{ display: 'block', width: '100%', height: 160 }}
          />
        </Paper>
      ))}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Markers
        </Typography>
        {log.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None yet — the wall marks the moment participants diverge.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {log.map((m, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: '.82rem' }}
              >
                {m.metric} · t={m.t.toFixed(1)} · {m.hi} +{m.hi_z.toFixed(1)}σ /{' '}
                {m.lo} {m.lo_z.toFixed(1)}σ (split {m.split.toFixed(1)}σ)
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

/** Draw one metric grid: auto-scaled traces + vertical markers. */
function drawMetric(
  canvas: HTMLCanvasElement,
  buf: LiveWallBuffer,
  metric: string,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.max(120, Math.round(canvas.clientWidth));
  const h = 160;
  if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const series = buf.series(metric);
  const pids = buf.participantIds();

  // Auto-scale y over the visible values (with a little padding); fall back to
  // a flat mid-line when there's nothing yet.
  let min = Infinity;
  let max = -Infinity;
  for (const pid of pids) {
    for (const v of series[pid] ?? []) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!isFinite(min) || !isFinite(max)) {
    min = 0;
    max = 1;
  }
  if (max - min < 1e-6) {
    min -= 0.5;
    max += 0.5;
  }
  const pad = (max - min) * 0.1;
  min -= pad;
  max += pad;

  const win = 240;
  const x = (i: number, n: number) => (w * (i + (win - n))) / (win - 1);
  const y = (v: number) => h * (1 - (v - min) / (max - min));

  // grid
  ctx.strokeStyle = 'rgba(128,128,128,0.18)';
  ctx.lineWidth = 1;
  for (let g = 0; g <= 3; g++) {
    const gy = (h * g) / 3;
    ctx.beginPath();
    ctx.moveTo(0, gy);
    ctx.lineTo(w, gy);
    ctx.stroke();
  }

  // markers (under the traces)
  for (const pm of buf.markersFor(metric)) {
    const mx = buf.markerX(pm);
    if (mx === null) continue;
    ctx.save();
    ctx.strokeStyle = 'rgba(160,160,160,0.9)';
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(mx * w, 0);
    ctx.lineTo(mx * w, h);
    ctx.stroke();
    ctx.restore();
  }

  // traces
  pids.forEach((pid, idx) => {
    const arr = series[pid] ?? [];
    if (arr.length < 2) return;
    ctx.strokeStyle = COLORS[idx % COLORS.length];
    ctx.lineWidth = 1.8;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    arr.forEach((v, i) => {
      const px = x(i, arr.length);
      const py = y(v);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
  });
}
